import type { Value } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import {
  ModelProfile,
  type ModelBinding,
  type ModelDescriptor,
  type ModelInterface,
  type ModelPlcOutput,
} from '@vitesse/proto/vitesse/v1/model_pb.js';
import type { ChildPorts, CheckOptions, GroupBinding, GroupSpec, LanguageDiagnostic, PlcOutput } from '@vitesse/statechart-host';
import type { ModelSource, ModelUpsert } from '../../core/bridge/connect-client';
import type { ModelIface } from '../../core/domain/model';
import { stripForEngine } from '../../sandbox/statechart/typescript';
import { checkDocument, handlesCommand, listFindings, readersOf, type WiringReader } from './model-check';
import { portTypes } from './port-types';

/*
 * A WHOLE MODEL, AS THE MODEL TOOLS SEE IT (Part 5 of docs/hierarchical-models-step-3-plan.md).
 *
 * `models.write` saves a group or a model with its own wiring: the root's document, the templates,
 * the children and the connections. It reads the whole model in one read (GetModelSource, with the
 * rest of the descriptor), turns it into a draft, puts the write's parts on it, checks the result,
 * and saves the parts that changed in one write. `models.read` lists a group from the same draft,
 * and `models.simulate` replays it. This module is the bridge: the proto into the draft and back,
 * the draft as the group spec the wiring check reads, and the check of every document and the
 * wiring together, so that one refusal lists every problem of the model.
 *
 * A constant goes through the draft in the proto's JSON shape, so a value no write touched goes
 * back exactly as it came: an INT stays an INT, a REAL with no fraction stays a REAL.
 */

/** A port of an interface. Fields the tools do not know are kept. */
export interface PortDeclaration {
  name: string;
  /** BOOL, INT, DINT, REAL, STRING, or a TypeScript type text for a port between two machines (Decision 4). */
  type: string;
  unit?: string;
  [field: string]: unknown;
}

/** A model's or a template's interface: its ports, and the root's commands. */
export interface DraftInterface {
  description?: string;
  inputs: PortDeclaration[];
  outputs: PortDeclaration[];
  commands: Array<{ name: string; params: string[] }>;
}

/** A constant, in the proto's JSON shape of `Value`. */
export type DraftLiteral = { boolVal: boolean } | { intVal: string } | { realVal: number } | { stringVal: string } | { rawVal: string };

/** An input's wiring: a path, or a constant. */
export type DraftBinding = { path: string } | { literal: DraftLiteral };

/** An output wired to the PLC, with the value it takes when its machine faults. */
export interface DraftPlcOutput {
  path: string;
  safe?: DraftLiteral;
}

export interface DraftChild {
  name: string;
  template: string;
  /**
   * An array of machines of the template (Step 4): from 1 to 256, `sensors/0` to `sensors/<count - 1>`.
   * Absent for a single child. A path in its wiring can hold `{i}`, the index, and `{n}`, the index
   * plus 1. Its inputs can also feed the platform's `enabled`.
   */
  count?: number;
  inputs: Record<string, DraftBinding>;
  outputs: Record<string, DraftPlcOutput>;
}

export interface DraftTemplate {
  scxml: string;
  iface: DraftInterface;
}

/** A model as the tools see it: the parts of its descriptor that a write can change. */
export interface ModelDraft {
  /** The root's document. */
  scxml: string;
  /** The root's interface. */
  iface: DraftInterface;
  /** The root's own wiring (`inputs = 15`): the one way an input of the root gets a value from outside. */
  inputs: Record<string, DraftBinding>;
  /** The root's PLC outputs (`outputs = 16`). */
  outputs: Record<string, DraftPlcOutput>;
  templates: Record<string, DraftTemplate>;
  children: DraftChild[];
  /**
   * `hard` for a hard model (`profile = 17`, docs/hierarchical-models-step-5-plan.md), for its
   * check only: each document keeps the rules of the hard profile, as the relay checks it. No
   * save sends it. The AI cannot set it (Decision 3 of the plan), and a save that does not name it
   * keeps it.
   */
  profile?: 'hard';
  /** Who gets a Slack message at each state change, and at the model's start and stop (`notify = 19`). Absent: nobody. */
  notify?: DraftNotify;
}

/** The Slack messages of a model: the send_dm tool of the org's Slack server, the person, and the time of a start or stop. */
export interface DraftNotify {
  slackTool: string;
  slackPerson: string;
  /** A declared output or input whose value is the time of the start and stop lines. Absent: the relay's clock. */
  slackTimeVariable?: string;
}

/** The check's profile of a saved model: `hard` for a hard model, and nothing for a general one. */
export function profileOf(descriptor: ModelDescriptor | undefined): { profile?: 'hard' } {
  return descriptor?.profile === ModelProfile.HARD ? { profile: 'hard' } : {};
}

/** The parts of a descriptor, by their update-mask names. */
export type DraftPart = 'scxml' | 'model_interface' | 'inputs' | 'outputs' | 'templates' | 'children' | 'notify';

export const emptyInterface = (): DraftInterface => ({ inputs: [], outputs: [], commands: [] });

/** A model that does not exist yet. */
export const emptyDraft = (): ModelDraft => ({
  scxml: '',
  iface: emptyInterface(),
  inputs: {},
  outputs: {},
  templates: {},
  children: [],
});

/** A constant as a tool's arguments give it: true, 3, 2.5 or "NO". */
export function literalFrom(value: string | number | boolean): DraftLiteral {
  if (typeof value === 'boolean') return { boolVal: value };
  if (typeof value === 'number') return Number.isInteger(value) ? { intVal: String(value) } : { realVal: value };
  return { stringVal: value };
}

/** A constant in the proto's JSON shape; undefined when it is not set. */
export function literalOf(value: Value | undefined): DraftLiteral | undefined {
  const v = value?.v;
  switch (v?.case) {
    case 'boolVal':
      return { boolVal: v.value };
    case 'intVal':
      return { intVal: String(v.value) };
    case 'realVal':
      return { realVal: v.value };
    case 'stringVal':
      return { stringVal: v.value };
    case 'rawVal':
      return { rawVal: btoa(String.fromCharCode(...v.value)) };
    default:
      return undefined;
  }
}

/** The proto's Value of a constant, as a message init. */
export function valueOf(literal: DraftLiteral): { v: { case: string; value: unknown } } {
  if ('boolVal' in literal) return { v: { case: 'boolVal', value: literal.boolVal } };
  if ('intVal' in literal) return { v: { case: 'intVal', value: BigInt(literal.intVal) } };
  if ('realVal' in literal) return { v: { case: 'realVal', value: literal.realVal } };
  if ('stringVal' in literal) return { v: { case: 'stringVal', value: literal.stringVal } };
  return { v: { case: 'rawVal', value: Uint8Array.from(atob(literal.rawVal), (c) => c.charCodeAt(0)) } };
}

/** A constant as the group runs it: a JavaScript value. Bytes are no port's value. */
function jsOf(literal: DraftLiteral | undefined): unknown {
  if (!literal) return undefined;
  if ('boolVal' in literal) return literal.boolVal;
  if ('intVal' in literal) return Number(literal.intVal);
  if ('realVal' in literal) return literal.realVal;
  if ('stringVal' in literal) return literal.stringVal;
  return undefined;
}

export function ifaceOf(i: ModelInterface | ModelIface | undefined): DraftInterface {
  return {
    description: i?.description ?? '',
    inputs: (i?.inputs ?? []).map((p) => ({ name: p.name, type: p.type, kind: p.kind, optional: p.optional, isArray: p.isArray })),
    outputs: (i?.outputs ?? []).map((p) => ({ name: p.name, type: p.type, unit: p.unit })),
    commands: (i?.commands ?? []).map((c) => ({ name: c.name, params: [...c.params] })),
  };
}

/** The interface as the descriptor holds it. */
export function ifaceInit(d: DraftInterface): ModelIface {
  return {
    description: d.description ?? '',
    inputs: d.inputs.map((p) => ({
      name: p.name,
      type: p.type,
      kind: typeof p.kind === 'number' ? p.kind : 0,
      optional: p.optional === true,
      isArray: p.isArray === true,
    })),
    outputs: d.outputs.map((p) => ({ name: p.name, type: p.type, unit: p.unit ?? '' })),
    commands: d.commands.map((c) => ({ name: c.name, params: [...c.params] })),
  };
}

function bindingOf(b: ModelBinding): DraftBinding | null {
  if (b.source.case === 'path') return { path: b.source.value };
  if (b.source.case === 'literal') return { literal: literalOf(b.source.value) ?? { stringVal: '' } };
  return null;
}

function plcOf(o: ModelPlcOutput): DraftPlcOutput {
  const safe = literalOf(o.safe);
  return { path: o.path, ...(safe ? { safe } : {}) };
}

function wiredInputs(map: Record<string, ModelBinding> | undefined): Record<string, DraftBinding> {
  const out: Record<string, DraftBinding> = {};
  for (const [port, b] of Object.entries(map ?? {})) {
    const draft = bindingOf(b);
    if (draft) out[port] = draft;
  }
  return out;
}

const plcs = (map: Record<string, ModelPlcOutput> | undefined): Record<string, DraftPlcOutput> =>
  Object.fromEntries(Object.entries(map ?? {}).map(([port, o]) => [port, plcOf(o)]));

/**
 * The draft of a saved model, from its source and the rest of its descriptor. A relay older than
 * Part 5 sends no descriptor: a single model is then read from its view, and a group is not.
 */
export function draftOf(
  source: ModelSource,
  view: {
    iface?: ModelIface;
    inputs?: string[];
    outputs?: string[];
    commands?: string[];
    children?: unknown[];
    wiring?: { inputs: unknown[]; outputs: unknown[] };
  },
): ModelDraft | null {
  const d: ModelDescriptor | undefined = source.descriptor;
  if (!d) {
    const grouped = (view.children ?? []).length > 0 || (view.wiring?.inputs.length ?? 0) > 0 || (view.wiring?.outputs.length ?? 0) > 0;
    if (grouped) return null;
    // A view with no interface still names its ports and commands.
    const named = (list: string[] | undefined) => (list ?? []).map((name) => ({ name, type: '' }));
    return {
      scxml: source.scxml,
      iface: view.iface
        ? ifaceOf(view.iface)
        : {
            ...emptyInterface(),
            inputs: named(view.inputs),
            outputs: named(view.outputs),
            commands: (view.commands ?? []).map((name) => ({ name, params: [] })),
          },
      inputs: {},
      outputs: {},
      templates: {},
      children: [],
    };
  }
  return {
    scxml: source.scxml,
    iface: d.modelInterface ? ifaceOf(d.modelInterface) : emptyInterface(),
    inputs: wiredInputs(d.inputs),
    outputs: plcs(d.outputs),
    templates: Object.fromEntries(
      Object.entries(d.templates).map(([name, t]) => [
        name,
        { scxml: t.scxml, iface: t.modelInterface ? ifaceOf(t.modelInterface) : emptyInterface() },
      ]),
    ),
    // The proto's 0 is a single child (Part 4 of docs/hierarchical-models-step-4-plan.md).
    children: d.children.map((c) => ({
      name: c.name,
      template: c.template,
      ...(c.count > 0 ? { count: c.count } : {}),
      inputs: wiredInputs(c.inputs),
      outputs: plcs(c.outputs),
    })),
    ...profileOf(d),
    ...(d.notify?.slackTool && d.notify.slackPerson
      ? {
          notify: {
            slackTool: d.notify.slackTool,
            slackPerson: d.notify.slackPerson,
            ...(d.notify.slackTimeVariable ? { slackTimeVariable: d.notify.slackTimeVariable } : {}),
          },
        }
      : {}),
  };
}

const bindingInit = (b: DraftBinding) =>
  'path' in b ? { source: { case: 'path' as const, value: b.path } } : { source: { case: 'literal' as const, value: valueOf(b.literal) } };
const plcInit = (o: DraftPlcOutput) => ({ path: o.path, ...(o.safe ? { safe: valueOf(o.safe) } : {}) });
const mapValues = <A, B>(map: Record<string, A>, f: (a: A) => B): Record<string, B> =>
  Object.fromEntries(Object.entries(map).map(([k, v]) => [k, f(v)]));

/** The write of a draft: every part for a create, else the parts that changed, by their mask names. */
export function upsertOf(
  draft: ModelDraft,
  parts: readonly (DraftPart | 'name')[],
  base: { name: string; desiredState: string },
): ModelUpsert {
  const all = parts.length === 0;
  const has = (p: DraftPart): boolean => all || parts.includes(p);
  return {
    name: base.name,
    scxml: draft.scxml,
    desiredState: base.desiredState,
    ...(has('model_interface') ? { modelInterface: ifaceInit(draft.iface) } : {}),
    ...(has('templates') ? { templates: mapValues(draft.templates, (t) => ({ scxml: t.scxml, modelInterface: ifaceInit(t.iface) })) } : {}),
    ...(has('children')
      ? {
          children: draft.children.map((c) => ({
            name: c.name,
            template: c.template,
            ...(c.count !== undefined ? { count: c.count } : {}),
            inputs: mapValues(c.inputs, bindingInit),
            outputs: mapValues(c.outputs, plcInit),
          })),
        }
      : {}),
    ...(has('inputs') ? { inputs: mapValues(draft.inputs, bindingInit) } : {}),
    ...(has('outputs') ? { outputs: mapValues(draft.outputs, plcInit) } : {}),
    // Named with no notify, it removes the one saved.
    ...(has('notify') && draft.notify ? { notify: { ...draft.notify } } : {}),
    updateMask: [...parts],
  } as ModelUpsert;
}

/** What one models.write sends: the root's document, and each part it names. A part it leaves out is kept. */
export interface DraftWrite {
  scxml: string;
  /** The root's interface: each list given replaces the one there. */
  iface?: { inputs?: PortDeclaration[]; outputs?: PortDeclaration[]; commands?: DraftInterface['commands'] };
  /** A root input's connection, a path or a constant; null disconnects it. An input not named keeps its own. */
  inputs?: Record<string, DraftBinding | null>;
  /** A root output's PLC symbol and safe value; null disconnects it. An output not named keeps its own. */
  outputs?: Record<string, DraftPlcOutput | null>;
  /** Templates by name: each replaces the one with its name, null removes it, and the others stay. */
  templates?: Record<string, DraftTemplate | null>;
  /** The complete list of children. */
  children?: DraftChild[];
  /** Who gets a Slack message at each state change; null removes it. Left out, the saved one stays. */
  notify?: DraftNotify | null;
}

/**
 * A write put on a saved model (or on an empty draft, for a create), and the parts of the
 * descriptor it changed, by their mask names. The root's document is always one of them. A port
 * that is no longer declared loses its connection.
 */
export function applyWrite(base: ModelDraft, w: DraftWrite): { draft: ModelDraft; parts: DraftPart[] } {
  const iface: DraftInterface = {
    ...base.iface,
    ...(w.iface?.inputs ? { inputs: w.iface.inputs } : {}),
    ...(w.iface?.outputs ? { outputs: w.iface.outputs } : {}),
    ...(w.iface?.commands ? { commands: w.iface.commands } : {}),
  };
  const kept = <T>(wiring: Record<string, T>, declared: PortDeclaration[] | undefined): Record<string, T> =>
    declared ? Object.fromEntries(Object.entries(wiring).filter(([port]) => declared.some((p) => p.name === port))) : { ...wiring };
  const inputs = kept(base.inputs, w.iface?.inputs);
  for (const [port, b] of Object.entries(w.inputs ?? {})) {
    if (b) inputs[port] = b;
    else delete inputs[port];
  }
  const outputs = kept(base.outputs, w.iface?.outputs);
  for (const [port, o] of Object.entries(w.outputs ?? {})) {
    if (o) outputs[port] = o;
    else delete outputs[port];
  }
  const templates = { ...base.templates };
  for (const [name, t] of Object.entries(w.templates ?? {})) {
    if (t) templates[name] = t;
    else delete templates[name];
  }
  const draft: ModelDraft = {
    scxml: w.scxml,
    iface,
    inputs,
    outputs,
    templates,
    children: w.children ?? base.children,
    // The saved profile: a write checks with it, and never sends it.
    ...(base.profile ? { profile: base.profile } : {}),
  };
  const notify = w.notify === undefined ? base.notify : (w.notify ?? undefined);
  if (notify) draft.notify = notify;

  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  const parts: DraftPart[] = ['scxml'];
  if (w.iface) parts.push('model_interface');
  if (!same(inputs, base.inputs)) parts.push('inputs');
  if (!same(outputs, base.outputs)) parts.push('outputs');
  if (w.templates) parts.push('templates');
  if (w.children) parts.push('children');
  if (!same(notify, base.notify)) parts.push('notify');
  return { draft, parts };
}

const names = (ports: readonly { name: string }[]): string[] => ports.map((p) => p.name);
const has = (record: object, key: string): boolean => Object.prototype.hasOwnProperty.call(record, key);

/**
 * The ports that the platform gives each machine of an array (Decisions 3 and 11 of
 * docs/hierarchical-models-step-4-plan.md): the parent starts and stops a machine with `enabled`,
 * and reads `faulted`. A template of an array does not declare them. These are copies of
 * statechart-host's `ARRAY_INPUTS` and `ARRAY_OUTPUTS`, because the page loads the host only on
 * first use, and a page builds these options when it opens a model. test/model-write-array.test.ts
 * holds the copies and the host's rules together.
 */
export const ARRAY_INPUTS: readonly string[] = ['enabled'];
export const ARRAY_OUTPUTS: readonly string[] = ['faulted'];

/** A child's ports as a document that reads it is checked with: its template's names and types. */
function portsOf(draft: ModelDraft, template: string): ChildPorts {
  const iface = draft.templates[template]?.iface;
  return { inputs: names(iface?.inputs ?? []), outputs: names(iface?.outputs ?? []), types: portTypes(iface) };
}

/**
 * The ports without `enabled` and `faulted`, as model-runner's `withoutPlatform` gives them. The
 * wiring check refuses a template of an array that declares one (`port-reserved`). Without them
 * here, the record of the array does not declare a field twice, so the type check gives no second
 * reason for the same fault.
 */
function withoutPlatform(ports: ChildPorts): ChildPorts {
  const platform = new Set([...ARRAY_INPUTS, ...ARRAY_OUTPUTS]);
  return { ...ports, inputs: ports.inputs.filter((p) => !platform.has(p)), outputs: ports.outputs.filter((p) => !platform.has(p)) };
}

/**
 * A child as its parent's document reads it. An array is a table: its count, and the inputs that its
 * wiring feeds, `enabled` among them when the wiring feeds it, as the host's `portsOf` gives them.
 * Each value has one writer, so the parent does not assign a wired input.
 */
function childPortsOf(draft: ModelDraft, child: DraftChild): ChildPorts {
  const ports = portsOf(draft, child.template);
  if (child.count === undefined) return ports;
  const wired = [...ports.inputs, ...ARRAY_INPUTS].filter((port) => has(child.inputs, port));
  return { ...withoutPlatform(ports), count: child.count, wired };
}

/**
 * A machine that a child's wiring names, as that child's document reads it: its outputs only, as
 * the host's `namedPorts` gives them. A named array is read-only too: its `enabled` is read, and
 * never assigned, or the namer and the root would be 2 writers of it.
 */
function namedPortsOf(draft: ModelDraft, other: DraftChild): ChildPorts {
  const ports = other.count === undefined ? portsOf(draft, other.template) : withoutPlatform(portsOf(draft, other.template));
  const named: ChildPorts = { inputs: [], outputs: ports.outputs, ...(ports.types ? { types: ports.types } : {}) };
  return other.count === undefined ? named : { ...named, count: other.count, wired: ARRAY_INPUTS };
}

/** The check options of each document of a draft: its ports, and the machines it reads by name. */
export function optionsOf(draft: ModelDraft): { root: CheckOptions; templates: Record<string, CheckOptions> } {
  const root: CheckOptions = {
    inputs: names(draft.iface.inputs),
    outputs: names(draft.iface.outputs),
    commands: draft.iface.commands.map((c) => c.name),
    types: portTypes(draft.iface),
    ...(draft.children.length > 0 ? { children: Object.fromEntries(draft.children.map((c) => [c.name, childPortsOf(draft, c)])) } : {}),
    // A hard model's profile goes to each of its documents, as the relay gives it (Part 4 of the Step 5 plan).
    ...(draft.profile ? { profile: draft.profile } : {}),
  };
  const templates: Record<string, CheckOptions> = {};
  // The children by name and by template, from one pass. A page builds these options from a saved
  // model, and a search of the children for each template costs templates times children.
  const childNamed = new Map<string, DraftChild>();
  const childrenOf = new Map<string, DraftChild[]>();
  for (const c of draft.children) {
    if (!childNamed.has(c.name)) childNamed.set(c.name, c);
    const list = childrenOf.get(c.template);
    if (list) list.push(c);
    else childrenOf.set(c.template, [c]);
  }
  for (const [name, t] of Object.entries(draft.templates)) {
    // A child of this template may name another machine through its wiring (the building's floors
    // name the elevator `lift`): its document reads that machine's outputs by that name.
    const refs: Record<string, ChildPorts> = {};
    for (const child of childrenOf.get(name) ?? []) {
      for (const [port, b] of Object.entries(child.inputs)) {
        const other = 'path' in b ? childNamed.get(b.path) : undefined;
        if (other) refs[port] = namedPortsOf(draft, other);
      }
    }
    templates[name] = {
      inputs: names(t.iface.inputs),
      outputs: names(t.iface.outputs),
      types: portTypes(t.iface),
      ...(Object.keys(refs).length > 0 ? { children: refs } : {}),
      ...(draft.profile ? { profile: draft.profile } : {}),
    };
  }
  return { root, templates };
}

/**
 * The draft as the group runs it and the wiring check reads it, with each document as the engine
 * reads it: the root's declared inputs when it has any, and its own wiring when it has any.
 */
export function specOf(draft: ModelDraft, text: { root: string; templates: Record<string, string> }): GroupSpec {
  const group = (b: DraftBinding): GroupBinding => ('path' in b ? { path: b.path } : { literal: jsOf(b.literal) ?? null });
  const plc = (o: DraftPlcOutput): PlcOutput => ({ path: o.path, safe: jsOf(o.safe) });
  const wired = Object.keys(draft.inputs).length > 0 || Object.keys(draft.outputs).length > 0;
  return {
    root: {
      scxml: text.root,
      ...(draft.iface.inputs.length > 0 ? { inputs: names(draft.iface.inputs) } : {}),
      outputs: names(draft.iface.outputs),
      types: portTypes(draft.iface),
      ...(wired ? { wiring: { inputs: mapValues(draft.inputs, group), outputs: mapValues(draft.outputs, plc) } } : {}),
    },
    templates: Object.fromEntries(
      Object.entries(draft.templates).map(([name, t]) => [
        name,
        { scxml: text.templates[name] ?? '', inputs: names(t.iface.inputs), outputs: names(t.iface.outputs), types: portTypes(t.iface) },
      ]),
    ),
    children: draft.children.map((c) => ({
      name: c.name,
      template: c.template,
      ...(c.count !== undefined ? { count: c.count } : {}),
      inputs: mapValues(c.inputs, group),
      outputs: mapValues(c.outputs, plc),
    })),
  };
}

const shown = (l: DraftLiteral | undefined): string => {
  const v = l === undefined ? undefined : 'rawVal' in l ? '(bytes)' : jsOf(l);
  return typeof v === 'string' ? JSON.stringify(v) : String(v);
};
const portsText = (ports: readonly { name: string; type: string }[]): string =>
  ports.map((p) => `${p.name} ${p.type}`).join(', ') || 'none';
const wiringText = (inputs: Record<string, DraftBinding>, outputs: Record<string, DraftPlcOutput>): string =>
  [
    ...Object.entries(inputs).map(([p, b]) => ('path' in b ? `${p} <- ${b.path}` : `${p} = ${shown(b.literal)}`)),
    ...Object.entries(outputs).map(([p, o]) => `${p} -> ${o.path} (safe ${shown(o.safe)})`),
  ].join('; ') || 'nothing wired';

/** An array child's count and the paths of its machines, as models.read shows them; empty for a single child. */
const arrayText = (c: DraftChild): string =>
  c.count === undefined ? '' : `, count ${c.count}: ${c.count === 1 ? `${c.name}/0` : `${c.name}/0 to ${c.name}/${c.count - 1}`}`;

/** True for a draft that runs as a group: children, or a root with wiring of its own (a group of one). */
export const runsAsGroup = (d: ModelDraft): boolean =>
  d.children.length > 0 || Object.keys(d.inputs).length > 0 || Object.keys(d.outputs).length > 0;

/** A group's templates, children and wiring, and the root's own wiring, as models.read lists them. Empty for a single model with none. */
export function groupNote(draft: ModelDraft): string {
  const lines: string[] = [];
  const templates = Object.entries(draft.templates);
  if (templates.length > 0) {
    lines.push('Templates (read one with doc set to its name; a template sent to models.write replaces the one of its name):');
    for (const [name, t] of templates) lines.push(`  ${name}: inputs ${portsText(t.iface.inputs)}; outputs ${portsText(t.iface.outputs)}`);
  }
  if (draft.children.length > 0) {
    lines.push('Children:');
    for (const c of draft.children) lines.push(`  ${c.name} (template ${c.template}${arrayText(c)}): ${wiringText(c.inputs, c.outputs)}`);
  }
  if (Object.keys(draft.inputs).length > 0 || Object.keys(draft.outputs).length > 0) {
    lines.push(`The root's own wiring: ${wiringText(draft.inputs, draft.outputs)}`);
  }
  return lines.length > 0 ? `\n${lines.join('\n')}` : '';
}

const UNKNOWN_NAME =
  'An unknown name is a misspelling, a missing port or variable, or a child that is not added: the model would boot, then fail every time that expression is evaluated.';

export interface DraftCheck {
  /** Each problem, under the document or the part it is in, ready to send. */
  refusing: string;
  refusingCount: number;
  /** The warnings, the same way; empty when there are none. */
  warnings: string;
}

/**
 * The check of a whole draft, as the relay checks a save: each document with its ports and the
 * machines it reads, every declared command handled, and the wiring with its types and readers.
 * Null when the language service could not be loaded: the relay is still the gate.
 */
export async function checkDraft(draft: ModelDraft, others: readonly WiringReader[], modelId: string): Promise<DraftCheck | null> {
  try {
    const { checkWiring, MAX_GROUP_SIZE, pathsFor } = await import('@vitesse/statechart-host');
    const options = optionsOf(draft);
    // A template no child uses yet does not run. A name in it can be one a child's wiring will
    // give (the building's floors read the elevator as `lift`), so an unknown name there is a
    // warning until a child uses the template; then it is checked with that child's names.
    const used = new Set(draft.children.map((c) => c.template));
    const unused = (name: string): boolean => !used.has(name);
    const docs: Array<[string, string, CheckOptions, boolean]> = [
      ['The root', draft.scxml, options.root, false],
      ...Object.entries(draft.templates).map(([name, t]): [string, string, CheckOptions, boolean] => [
        `Template "${name}"`,
        t.scxml,
        options.templates[name] ?? {},
        unused(name),
      ]),
    ];
    const refusing: string[] = [];
    const warnings: string[] = [];
    let count = 0;
    let unknownName = false;
    for (const [label, text, opts, draftOnly] of docs) {
      const checked = await checkDocument(text, opts);
      if (!checked) return null;
      const later = draftOnly ? checked.refusing.filter((d: LanguageDiagnostic) => d.code === 'unknown-identifier') : [];
      const refused = checked.refusing.filter((d: LanguageDiagnostic) => !later.includes(d));
      if (refused.length > 0) {
        count += refused.length;
        refusing.push(`${label}:\n${listFindings(text, refused)}`);
        unknownName ||= refused.some((d: LanguageDiagnostic) => d.code === 'unknown-identifier');
      }
      const warned = [...later, ...checked.warnings];
      if (warned.length > 0) {
        const note = later.length > 0 ? ' (no child uses it yet; an unknown name is checked again when one does)' : '';
        warnings.push(`${label}${note}:\n${listFindings(text, warned)}`);
      }
    }

    const unhandled = draft.iface.commands.filter((c) => !handlesCommand(draft.scxml, c.name)).map((c) => c.name);
    if (unhandled.length > 0) {
      count += unhandled.length;
      refusing.push(
        `The commands:\n${unhandled.map((c) => `  "${c}" is declared, and no transition of the root handles it: add one with event "${c}", or remove the command`).join('\n')}`,
      );
    }

    // The wiring, over the documents as the engine reads them.
    const strip = async (text: string): Promise<string> => (await stripForEngine(text)).text;
    const templates: Record<string, string> = {};
    for (const [name, t] of Object.entries(draft.templates)) templates[name] = await strip(t.scxml);
    const spec = specOf(draft, { root: await strip(draft.scxml), templates });
    const found = checkWiring(spec, { modelId, readers: readersOf(others, modelId, pathsFor, MAX_GROUP_SIZE) });
    const wiringErrors = found.filter((f) => f.severity === 'error').map((f) => `  ${f.message}`);
    // A path whose type the page does not know is the relay's to judge: it reads the symbol's descriptor.
    const wiringWarnings = found.filter((f) => f.severity === 'warning' && f.code !== 'type-unknown').map((f) => `  ${f.message}`);
    if (wiringErrors.length > 0) {
      count += wiringErrors.length;
      refusing.push(`The wiring:\n${wiringErrors.join('\n')}`);
    }
    if (wiringWarnings.length > 0) warnings.push(`The wiring:\n${wiringWarnings.join('\n')}`);

    return {
      refusing: refusing.length > 0 ? `${refusing.join('\n')}${unknownName ? `\n${UNKNOWN_NAME}` : ''}` : '',
      refusingCount: count,
      warnings: warnings.join('\n'),
    };
  } catch {
    return null;
  }
}
